// Logique pure du fil de commentaires (testée sans DOM).
import type { Author, Comment, Mention } from '@feedbaacks/sdk'
import type { Translate } from './i18n'

/** Réactions rapides, comme Faast. */
export const QUICK_EMOJIS = ['👍', '❤️', '✅', '👀', '🎉']

/** Durée de vie d'un « en train d'écrire » sans nouveau signal. */
export const TYPING_TTL_MS = 6000
/** Intervalle minimal entre deux signaux de saisie envoyés. */
export const TYPING_THROTTLE_MS = 3000

/** Fil : commentaires de premier niveau, et réponses rangées sous leur parent. */
export function threadOf(list: Comment[]): { top: Comment[]; replies: Map<string, Comment[]> } {
  const ids = new Set(list.map((c) => c.id))
  const top: Comment[] = []
  const replies = new Map<string, Comment[]>()
  for (const c of list) {
    // Une réponse dont le parent n'est plus là remonte au premier niveau.
    if (c.parent_id && ids.has(c.parent_id)) replies.set(c.parent_id, [...(replies.get(c.parent_id) ?? []), c])
    else top.push(c)
  }
  return { top, replies }
}

/** Le « @mot » en cours de saisie juste avant le curseur, s'il y en a un. */
export function mentionQuery(text: string, caret: number): { start: number; query: string } | null {
  const before = text.slice(0, caret)
  const m = /(^|\s)@([^\s@]{0,40})$/.exec(before)
  if (!m) return null
  return { start: caret - m[2].length - 1, query: m[2] }
}

/** Remplace le « @mot » en cours par « @Nom ». Rend le texte et le curseur. */
export function insertMention(text: string, start: number, caret: number, name: string): { text: string; caret: number } {
  const token = `@${name} `
  return { text: text.slice(0, start) + token + text.slice(caret), caret: start + token.length }
}

/** Les mentions choisies encore présentes dans le texte envoyé. */
export function keptMentions(text: string, picked: Array<{ user: Author; text: string }>): Mention[] {
  const seen = new Set<string>()
  const out: Mention[] = []
  for (const p of picked) {
    if (!seen.has(p.user.id) && text.includes(p.text)) {
      seen.add(p.user.id)
      out.push({ user_id: p.user.id, text: p.text })
    }
  }
  return out
}

/** « Sam écrit… », « Sam, Léo écrivent… ». */
export function typingLabel(names: string[], t: Translate): string {
  if (!names.length) return ''
  return names.length === 1 ? t('typing_one', { name: names[0] }) : t('typing_many', { names: names.join(', ') })
}

/** Le compteur de réponses, au singulier ou au pluriel. */
export function replyCountLabel(count: number, t: Translate): string {
  return t(count > 1 ? 'comment_reply_count_other' : 'comment_reply_count_one', { count })
}
